// Centralni stav faktury + jednoduchy pub/sub.
// Vsechny zmeny tecou pres update() / setItem() / addItem() / removeItem(),
// po kazde zmene se zavolaji vsichni odberatele.

import { todayIso, addDaysIso } from './dates.js';

/**
 * @typedef {import('./calc.js').LineItem} LineItem
 *
 * @typedef {Object} Party
 * @property {string} name
 * @property {string} address  vicaradkova adresa
 * @property {string} ico
 * @property {string} dic
 * @property {string} bank
 * @property {string} account
 *
 * @typedef {Object} Dates
 * @property {string} plneni       ISO YYYY-MM-DD
 * @property {string} vystaveni
 * @property {string} splatnosti
 * @property {boolean} splatnostiDirty   true => uzivatel ji rucne menil
 *
 * @typedef {Object} Payment
 * @property {string} ks       konstantni symbol
 * @property {string} forma    forma uhrady
 *
 * @typedef {Object} Meta
 * @property {boolean} isVatPayer
 * @property {string} currency
 *
 * @typedef {Object} InvoiceState
 * @property {string} number
 * @property {Party} dodavatel
 * @property {Party} odberatel
 * @property {Dates} dates
 * @property {Payment} payment
 * @property {LineItem[]} items
 * @property {Meta} meta
 */

/** @type {InvoiceState} */
const defaults = createDefaults();

/** @type {InvoiceState} */
let state = clone(defaults);

/** @type {Set<(s: InvoiceState) => void>} */
const listeners = new Set();

/** Vrati hluboky klon vychoziho stavu (nemenne sablony). */
export function getDefaults() {
  return clone(defaults);
}

/** Aktualni snapshot stavu (pouze pro cteni; nepristupovat k mutacim). */
export function getState() {
  return state;
}

/**
 * Prihlasi listener, vrati funkci pro odhlaseni.
 * @param {(s: InvoiceState) => void} fn
 */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Nahradi cely stav (napr. po nacteni z localStorage). */
export function replaceState(next) {
  state = mergeWithDefaults(next);
  notify();
}

/**
 * Patchuje stav (jen prvni uroven; pro vnorene objekty pouzij update napr. {dodavatel: {...state.dodavatel, name}}).
 * @param {Partial<InvoiceState>} patch
 */
export function update(patch) {
  state = { ...state, ...patch };
  notify();
}

/** Patch konkretni polozky podle id. */
export function setItem(id, patch) {
  state = {
    ...state,
    items: state.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
  };
  notify();
}

/** Prida prazdnou polozku na konec. */
export function addItem() {
  state = {
    ...state,
    items: [...state.items, makeEmptyItem()],
  };
  notify();
}

/** Odstrani polozku podle id. Posledni polozku nemaze. */
export function removeItem(id) {
  if (state.items.length <= 1) return;
  state = {
    ...state,
    items: state.items.filter((it) => it.id !== id),
  };
  notify();
}

/**
 * Nastavi datum a pripadne automaticky doposune splatnost (+14 dni),
 * pokud uzivatel splatnost zatim rucne nemenil.
 * @param {"plneni"|"vystaveni"|"splatnosti"} key
 * @param {string} iso
 */
export function setDate(key, iso) {
  const dates = { ...state.dates, [key]: iso };
  if (key === 'splatnosti') {
    dates.splatnostiDirty = true;
  } else if (key === 'vystaveni' && !state.dates.splatnostiDirty) {
    dates.splatnosti = addDaysIso(iso, 14);
  }
  state = { ...state, dates };
  notify();
}

/** Reset stavu na default + nove cislo faktury (predane volajicim). */
export function reset(nextNumber) {
  state = clone(defaults);
  if (nextNumber) state.number = nextNumber;
  notify();
}

function notify() {
  for (const fn of listeners) fn(state);
}

function createDefaults() {
  const today = todayIso();
  return {
    number: '',
    dodavatel: {
      name: 'JUDr. Gabriela Kretschmannová',
      address: 'Jeremenkova 3\n742 42 Šenov u Nového Jičína',
      ico: '63714353',
      dic: 'CZ5753090376',
      bank: 'Raiffeisen bank',
      account: '712570036/5500',
    },
    odberatel: {
      name: '',
      address: '',
      ico: '',
      dic: '',
      bank: '',
      account: '',
    },
    dates: {
      plneni: today,
      vystaveni: today,
      splatnosti: addDaysIso(today, 14),
      splatnostiDirty: false,
    },
    payment: {
      ks: '308',
      forma: 'převodem',
    },
    items: [makeEmptyItem()],
    meta: {
      isVatPayer: true,
      currency: 'Kč',
    },
  };
}

function makeEmptyItem() {
  return {
    id: cryptoId(),
    name: '',
    qty: 1,
    unit: 'ks',
    price: 0,
    vat: 21,
    note: '',
  };
}

function cryptoId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

// Pri nacitani z localStorage muze byt schema starsi -> doplnime chybejici klice.
function mergeWithDefaults(input) {
  const base = clone(defaults);
  if (!input || typeof input !== 'object') return base;
  return {
    ...base,
    ...input,
    dodavatel: { ...base.dodavatel, ...(input.dodavatel ?? {}) },
    odberatel: { ...base.odberatel, ...(input.odberatel ?? {}) },
    dates: { ...base.dates, ...(input.dates ?? {}) },
    payment: { ...base.payment, ...(input.payment ?? {}) },
    meta: { ...base.meta, ...(input.meta ?? {}) },
    items: Array.isArray(input.items) && input.items.length
      ? input.items.map((it) => ({ ...makeEmptyItem(), ...it }))
      : [makeEmptyItem()],
  };
}
